import { gsap } from 'gsap';

/**
 * Path drawing for SVG elements that declare pathLength="1" and class "draw".
 * Only valid inside uniformly scaled SVGs (no non-scaling-stroke), which is how every
 * diagram in this project is authored.
 */
export function draw(
  targets: Element | Element[] | NodeListOf<Element>,
  drawn: boolean,
  { duration = 1, delay = 0, stagger = 0, ease = 'power2.inOut', immediate = false } = {},
): gsap.core.Tween {
  const offset = drawn ? 0 : 1;
  if (immediate) return gsap.set(targets, { strokeDashoffset: offset });
  return gsap.to(targets, { strokeDashoffset: offset, duration, delay, stagger, ease, overwrite: 'auto' });
}

/**
 * Samples a path once into a lookup table. Reading geometry (getPointAtLength) inside an animation
 * frame forces a synchronous layout after every attribute write, so frame loops read from this
 * table instead of the DOM.
 */
export function sampler(path: SVGGeometryElement, samples = 240): (t: number) => { x: number; y: number } {
  const length = path.getTotalLength();
  const xs = new Float32Array(samples + 1);
  const ys = new Float32Array(samples + 1);
  for (let i = 0; i <= samples; i += 1) {
    const point = path.getPointAtLength((i / samples) * length);
    xs[i] = point.x;
    ys[i] = point.y;
  }
  const point = { x: 0, y: 0 };
  return (t: number) => {
    const f = Math.min(Math.max(t, 0), 1) * samples;
    const i = Math.min(Math.floor(f), samples - 1);
    const k = f - i;
    point.x = xs[i] + (xs[i + 1] - xs[i]) * k;
    point.y = ys[i] + (ys[i + 1] - ys[i]) * k;
    return point;
  };
}

/** Moves a marker continuously along a path. Returns the tween so the caller can register it as a loop. */
export function travel(marker: SVGGraphicsElement, path: SVGGeometryElement, duration: number, delay = 0): gsap.core.Tween {
  const at = sampler(path);
  const proxy = { t: 0 };
  const place = (): void => {
    const p = at(proxy.t);
    marker.setAttribute('transform', `translate(${p.x} ${p.y})`);
  };
  place();
  return gsap.to(proxy, { t: 1, duration, delay, ease: 'none', repeat: -1, onUpdate: place });
}

/** A marker that runs along a path and fades in and out, repeating: energy flowing through a connection. */
export function pulse(
  marker: SVGGraphicsElement,
  path: SVGGeometryElement,
  { duration = 1.8, delay = 0, repeatDelay = 1, ease = 'power1.inOut' } = {},
): gsap.core.Tween {
  const at = sampler(path);
  const proxy = { t: 0 };
  marker.style.opacity = '0';
  return gsap.to(proxy, {
    t: 1,
    duration,
    delay,
    repeat: -1,
    repeatDelay,
    ease,
    onUpdate: () => {
      const point = at(proxy.t);
      marker.setAttribute('transform', `translate(${point.x} ${point.y})`);
      marker.style.opacity = String(Math.sin(proxy.t * Math.PI));
    },
  });
}
